// ===================================================================
// localizedContent.ts — locale-aware access to bilingual content fields
// ===================================================================
// Vocabulary/grammar/sentence data always carries its French translation
// (the pivot language content was authored in) plus an optional English
// sibling field (translationEn/explanationEn/exampleEn/english). These
// helpers pick the field matching the interface language, falling back to
// French whenever the English sibling is absent — which today is always,
// since no content has been translated yet, so using these helpers changes
// nothing visible until *En fields start getting filled in.
import { WordData, GrammarItem, Sentence } from '../types/types';

type ContentLanguage = string; // 'fr' | 'en' | future interface languages

export function getWordTranslation(
  word: Pick<WordData | GrammarItem, 'translation' | 'translationEn' | 'fr'>,
  language: ContentLanguage
): string {
  if (language === 'en' && word.translationEn) return word.translationEn;
  return word.translation ?? word.fr ?? '';
}

export function getWordExplanation(
  word: Pick<WordData | GrammarItem, 'explanation' | 'explanationEn'>,
  language: ContentLanguage
): string | undefined {
  if (language === 'en' && word.explanationEn) return word.explanationEn;
  return word.explanation;
}

export function getWordExample(
  word: Pick<WordData | GrammarItem, 'example' | 'exampleEn'>,
  language: ContentLanguage
): string | undefined {
  if (language === 'en' && word.exampleEn) return word.exampleEn;
  return word.example;
}

export function getWordQuiz(
  word: Pick<WordData, 'quiz'>,
  language: ContentLanguage
): WordData['quiz'] {
  const quiz = word.quiz;
  if (!quiz) return quiz;
  if (language !== 'en') return quiz;
  return {
    ...quiz,
    question: quiz.questionEn ?? quiz.question,
    options: quiz.optionsAreGlyphs ? quiz.options : (quiz.optionsEn ?? quiz.options),
    explanation: quiz.explanationEn ?? quiz.explanation,
  };
}

export function getWordReading(
  word: Pick<WordData, 'reading' | 'readingEn'>,
  language: ContentLanguage
): string | undefined {
  if (language === 'en' && word.readingEn) return word.readingEn;
  return word.reading;
}

export function getGrammarExamples(
  item: Pick<GrammarItem, 'examples' | 'examplesEn'>,
  language: ContentLanguage
): string[] | undefined {
  if (language === 'en' && item.examplesEn) return item.examplesEn;
  return item.examples;
}

export function getSentenceTranslation(
  sentence: Pick<Sentence, 'french' | 'english'>,
  language: ContentLanguage
): string {
  if (language === 'en' && sentence.english) return sentence.english;
  return sentence.french;
}
